import React from 'react';
import { motion } from 'framer-motion';
import { Building2Icon, MapPinIcon, HeartIcon, HistoryIcon, GraduationCapIcon, GemIcon, HotelIcon, TrendingUpIcon, ArrowRightIcon } from 'lucide-react';
const sectionHeader = (tag: string, title: string, accent: string, description?: string) => <motion.div initial={{
  opacity: 0,
  y: 30
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true
}} transition={{
  duration: 0.8
}} className="text-center mb-16" data-id="element-601" data-mp="ve865xmh1l0l">
    <p className="text-[#d4a017]/40 tracking-[0.3em] text-[10px] uppercase mb-5 font-light" data-id="element-602" data-mp="1n2jpqxmaui1q">
      {tag}
    </p>
    <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-[#f5f0e8] mb-6 tracking-tight" data-id="element-603" data-mp="1e3oirjbfl3xd">
      {title} <span className="text-[#d4a017]" data-id="element-604" data-mp="7nck7h4p5k19">{accent}</span>
    </h2>
    <div className="w-16 h-px bg-gradient-to-r from-transparent via-[#d4a017]/30 to-transparent mx-auto mb-6" data-id="element-605" data-mp="7lj8voj52bmx"></div>
    {description && <p className="text-[#f5f0e8]/40 font-light max-w-2xl mx-auto leading-relaxed" data-id="element-606" data-mp="2gpwgweuwy1ij">
        {description}
      </p>}
  </motion.div>;
const partnerCard = (icon: React.ReactNode, title: string, body: string, delay: number) => <motion.div initial={{
  opacity: 0,
  y: 20
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true
}} transition={{
  duration: 0.6,
  delay
}} className="border border-[#d4a017]/15 bg-[#0f0f23]/40 p-7" data-id="element-607" data-mp="27p805ce4xn1y">
    <div className="text-[#d4a017]/30 mb-4" data-id="element-608" data-mp="22rewaq0b154u">{icon}</div>
    <h4 className="font-serif text-lg text-[#f5f0e8] mb-3" data-id="element-609" data-mp="1ozi7autxnbco">{title}</h4>
    <p className="text-[#f5f0e8]/40 text-sm font-light leading-relaxed" data-id="element-610" data-mp="1ox5ipyto62qn">
      {body}
    </p>
  </motion.div>;
/* ─── SPONSORS ─── */
export function SponsorsSection() {
  return <section id="sponsors" className="py-24 md:py-32 bg-[#0a0a0f] relative overflow-hidden" data-id="element-611" data-mp="1f1kia5491aj0">
      <div className="absolute inset-0 bg-gradient-to-b from-[#1a1a2e]/8 to-transparent" data-id="element-612" data-mp="1k28agu47rxet"></div>
      <div className="container mx-auto px-6 relative z-10 max-w-5xl" data-id="element-613" data-mp="3lqma29t1n1os">
        {sectionHeader('Section 11', 'Sponsor', 'Opportunities', 'Premium brand alignment with heritage, excellence, and multicultural innovation.')}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-614" data-mp="37z7phyjhmsfm">
          {partnerCard(<Building2Icon className="w-7 h-7" data-id="element-615" data-mp="8hhrs88rbzmo" />, 'Hosted Tables', 'Private branded tables for 10 guests with premium hospitality, dedicated service, and branded photo moments throughout the evening.', 0)}
          {partnerCard(<Building2Icon className="w-7 h-7" data-id="element-616" data-mp="3qp79i70h1hc6" />, 'Venue Banners', 'Premium banner placement throughout the venue — entrance corridor, main room, and exit experience. High-visibility, high-impact.', 0.05)}
          {partnerCard(<Building2Icon className="w-7 h-7" data-id="element-617" data-mp="255kemg5rj3vb" />, 'Founder Wall', 'Permanent recognition on the Founder Wall — a digital and physical display honoring the partners who made this experience possible.', 0.1)}
          {partnerCard(<Building2Icon className="w-7 h-7" data-id="element-618" data-mp="2f1ndnd5bime1" />, 'Scene Sponsorship', 'Sponsor a specific portal or scene — your brand woven into the narrative itself. The most immersive form of brand integration.', 0.15)}
          {partnerCard(<Building2Icon className="w-7 h-7" data-id="element-619" data-mp="2st20jv2cj7qd" />, 'Premium Hospitality', 'VIP reception hosting, private chef interaction, behind-the-scenes tours, and exclusive access to the creative team.', 0.2)}
          {partnerCard(<Building2Icon className="w-7 h-7" data-id="element-620" data-mp="13h02ccb5j3jj" />, 'Donor Recognition', 'Tax-deductible charitable contribution with full recognition across event materials, press releases, and community communications.', 0.25)}
        </div>

        {/* Sponsors & Patronage Video */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="mt-12" data-id="element-621" data-mp="2twk7skxqacyx">
          <div className="border border-[#d4a017]/20 bg-[#0f0f23]/80 backdrop-blur-sm" data-id="element-622" data-mp="2b6kq4j47w05j">
            <div className="aspect-video bg-[#0a0a0f] relative overflow-hidden" data-id="element-623" data-mp="2b4rgqw27z1tu">
              <iframe className="w-full h-full" src="https://www.youtube.com/embed/bJZtX1yNRXk" title="Sponsors & Patronage — Life in the Sikh Kingdoms" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen data-id="element-624" data-mp="2ozae2t3b3f5a"></iframe>
            </div>
            <div className="p-6 border-t border-[#d4a017]/10" data-id="element-625" data-mp="6yzstgymjx7u">
              <h3 className="font-serif text-lg text-[#f5f0e8] mb-1" data-id="element-626" data-mp="brj4279uag9h">
                Sponsors &amp; Patronage
              </h3>
              <p className="text-[#f5f0e8]/40 text-sm font-light" data-id="element-627" data-mp="3u6tnwwvy9tjc">
                How sponsorship and patronage bring this immersive cultural
                experience to life.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}
/* ─── ONTARIO TOURISM ─── */
export function OntarioTourismSection() {
  return <section id="ontario-tourism" className="py-24 md:py-32 bg-[#0a0a0f] relative overflow-hidden" data-id="element-628" data-mp="gqvrat4mhpcl">
      <div className="container mx-auto px-6 relative z-10 max-w-5xl" data-id="element-629" data-mp="loe2kq644gnr">
        {sectionHeader('Section 12', 'Ontario', 'Tourism', 'A cultural tourism asset that positions Toronto as a global destination for immersive heritage experiences.')}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-630" data-mp="2mcib3xsgk3q1">
          {partnerCard(<MapPinIcon className="w-7 h-7" data-id="element-631" data-mp="2ibq0b03xtqrn" />, 'Tourism Relevance', 'Life in the Sikh Kingdoms is a destination experience — drawing visitors from across Ontario, Canada, and internationally. It combines culture, cuisine, and technology in a format that appeals to the modern cultural tourist.', 0)}
          {partnerCard(<MapPinIcon className="w-7 h-7" data-id="element-632" data-mp="1ynz4rnsyy10l" />, 'Cultural Significance', "This experience celebrates one of Canada's most vibrant diaspora communities. It positions Ontario as a leader in multicultural arts and immersive cultural experiences — a story the province can proudly tell.", 0.1)}
          {partnerCard(<MapPinIcon className="w-7 h-7" data-id="element-633" data-mp="3ozwq7eiah9cr" />, 'Education & Community', 'School field trips, community nights, and youth programs create lasting educational value. This is not just entertainment — it is a living classroom that connects generations to heritage.', 0.2)}
          {partnerCard(<MapPinIcon className="w-7 h-7" data-id="element-634" data-mp="55iauh35tp3f" />, 'Premium Event Potential', "Corporate events, diplomatic receptions, and private experiences generate premium tourism revenue. Each event brings visitors to Toronto's entertainment district, supporting local hotels, restaurants, and businesses.", 0.3)}
        </div>
      </div>
    </section>;
}
/* ─── GURDWARAS ─── */
export function GurdwarasSection() {
  return <section id="gurdwaras" className="py-24 md:py-32 bg-[#0a0a0f] relative overflow-hidden" data-id="element-635" data-mp="3iiv41rejj5ab">
      <div className="absolute inset-0 bg-gradient-to-b from-[#1a1a2e]/8 to-transparent" data-id="element-636" data-mp="3mjng0sesroaa"></div>
      <div className="container mx-auto px-6 relative z-10 max-w-5xl" data-id="element-637" data-mp="xw0bzckbpge7">
        {sectionHeader('Section 13', 'Gurdwaras &', 'Community', 'Cultural preservation, youth engagement, and heritage celebration — rooted in community.')}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-638" data-mp="1izjbj9nfvpqi">
          {partnerCard(<HeartIcon className="w-7 h-7" data-id="element-639" data-mp="1xnnu8dmhs19a" />, 'Cultural & Community Value', 'This experience brings Sikh history to life in a way that resonates with all generations — from elders who remember the stories to youth discovering their heritage for the first time. It is a bridge between past and future.', 0)}
          {partnerCard(<HeartIcon className="w-7 h-7" data-id="element-640" data-mp="2gm06474sfp8k" />, 'Youth Engagement', 'Interactive technology, immersive storytelling, and sensory design speak the language of the next generation. This is heritage preservation through innovation — meeting young people where they are.', 0.1)}
          {partnerCard(<HeartIcon className="w-7 h-7" data-id="element-641" data-mp="hapafljiroam" />, 'Hosted Community Nights', 'Gurdwaras can host dedicated community nights — group tickets, youth programs, and elder appreciation evenings. A meaningful outing that celebrates shared heritage.', 0.2)}
          {partnerCard(<HeartIcon className="w-7 h-7" data-id="element-642" data-mp="2o59fw1h7hpy5" />, 'Support Opportunities', 'Community organizations can support the experience through group ticket purchases, volunteer coordination, outreach, and partnership with the ROM Sikh Art and Culture Gallery fund.', 0.3)}
        </div>
      </div>
    </section>;
}
/* ─── PREVIOUS SPONSORS ─── */
export function PreviousSponsorsSection() {
  return <section id="previous-sponsors" className="py-24 md:py-32 bg-[#0a0a0f] relative overflow-hidden" data-id="element-643" data-mp="257gbeuqxp7yn">
      <div className="container mx-auto px-6 relative z-10 max-w-5xl" data-id="element-644" data-mp="3uluw93yu0aov">
        {sectionHeader('Section 14', 'Previous', 'Sponsors', 'The next chapter of a legacy — from Art of the Sikh Kingdoms to Life in the Sikh Kingdoms.')}

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="border border-[#d4a017]/15 bg-[#0f0f23]/40 p-8 md:p-10 mb-8" data-id="element-645" data-mp="y373r89jmes7">
          <p className="text-[#f5f0e8]/50 font-light leading-relaxed mb-6" data-id="element-646" data-mp="1h1b1z7nw6tf8">
            In August 2000, the Royal Ontario Museum hosted{' '}
            <strong className="text-[#d4a017]" data-id="element-647" data-mp="13105np3tcmmo">
              "Art of the Sikh Kingdoms"
            </strong>{' '}
            — the second most popular exhibition in ROM history. That exhibition
            was made possible by visionary sponsors who believed in the power of
            Sikh heritage to captivate a global audience.
          </p>
          <p className="text-[#f5f0e8]/50 font-light leading-relaxed mb-6" data-id="element-648" data-mp="3l6jgjufwkhyt">
            Life in the Sikh Kingdoms is the next chapter of that legacy. It
            takes the same heritage and brings it into the immersive age — 360°
            projections, interactive technology, royal cuisine, and
            multi-sensory storytelling.
          </p>
          <p className="text-[#f5f0e8]/50 font-light leading-relaxed" data-id="element-649" data-mp="3p6jvdg6wyv6e">
            Previous sponsors of Art of the Sikh Kingdoms are natural partners
            for this experience. The continuity from past cultural support into
            an immersive future is a powerful story — and one that resonates
            with communities, media, and institutions alike.
          </p>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="border border-[#d4a017]/20 bg-[#d4a017]/5 p-8 text-center" data-id="element-650" data-mp="36g2a1woev1v2">
          <p className="text-[#d4a017] italic font-light text-lg leading-relaxed" data-id="element-651" data-mp="mncwjp9up87j">
            "The sponsors who made Art of the Sikh Kingdoms possible changed how
            the world sees Sikh heritage. Now, a new generation of partners can
            take that legacy further than ever before."
          </p>
        </motion.div>

        {/* Previous Supporters Video */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="mt-12" data-id="element-652" data-mp="3rd4kegvi4ny">
          <div className="border border-[#d4a017]/20 bg-[#0f0f23]/80 backdrop-blur-sm" data-id="element-653" data-mp="1k76ad2iicai5">
            <div className="aspect-video bg-[#0a0a0f] relative overflow-hidden" data-id="element-654" data-mp="1o8hx909zyhmn">
              <iframe className="w-full h-full" src="https://www.youtube.com/embed/utjPWynQXrw" title="Previous Supporters of Art of the Sikh Kingdoms" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen data-id="element-655" data-mp="38no75h366lqt"></iframe>
            </div>
            <div className="p-6 border-t border-[#d4a017]/10" data-id="element-656" data-mp="2pyretirc23b8">
              <h3 className="font-serif text-lg text-[#f5f0e8] mb-1" data-id="element-657" data-mp="2kydfcp3ss1ot">
                Art of the Sikh Kingdoms — Legacy &amp; Continuity
              </h3>
              <p className="text-[#f5f0e8]/40 text-sm font-light" data-id="element-658" data-mp="2gze0abocqojn">
                Why previous supporters of Art of the Sikh Kingdoms are natural
                partners for this next chapter.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}
/* ─── SCHOOLS ─── */
export function SchoolsSection() {
  return <section id="schools" className="py-24 md:py-32 bg-[#0a0a0f] relative overflow-hidden" data-id="element-659" data-mp="332dh3z2zftay">
      <div className="absolute inset-0 bg-gradient-to-b from-[#1a1a2e]/8 to-transparent" data-id="element-660" data-mp="37z3wek6yqkvb"></div>
      <div className="container mx-auto px-6 relative z-10 max-w-5xl" data-id="element-661" data-mp="2tzudvdvabeo6">
        {sectionHeader('Section 15', 'Schools &', 'Education', 'A living classroom where history is not read — it is experienced.')}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-662" data-mp="2p1j75c0adka6">
          {partnerCard(<GraduationCapIcon className="w-7 h-7" data-id="element-663" data-mp="2ba4lexezv4zf" />, 'Educational Value', 'Students engage with 200+ years of Sikh history through immersive technology, sensory storytelling, and interactive experiences. This is experiential learning at its most powerful — history that students feel, not just study.', 0)}
          {partnerCard(<GraduationCapIcon className="w-7 h-7" data-id="element-664" data-mp="26d4dsi2hxu67" />, 'Learning Outcomes', 'Aligned with Ontario curriculum standards for history, social studies, and multicultural education. Students explore themes of leadership, pluralism, innovation, and resilience through the lens of the Sikh Empire.', 0.1)}
          {partnerCard(<GraduationCapIcon className="w-7 h-7" data-id="element-665" data-mp="1rnjj2zns76f2" />, 'School Field Trip Version', 'A dedicated daytime format designed for school groups — shorter duration, educator guide, pre-visit materials, and post-visit discussion prompts. Talkback sessions with historians and creators available.', 0.2)}
          {partnerCard(<GraduationCapIcon className="w-7 h-7" data-id="element-666" data-mp="2aeaojq4neco6" />, 'Cultural Learning', 'Students from all backgrounds discover the richness of Sikh heritage — its art, architecture, cuisine, music, and values. A powerful tool for building empathy, understanding, and multicultural appreciation.', 0.3)}
        </div>
      </div>
    </section>;
}
/* ─── WEDDING PLANNERS ─── */
export function WeddingPlannersSection() {
  return <section id="wedding-planners" className="py-24 md:py-32 bg-[#0a0a0f] relative overflow-hidden" data-id="element-667" data-mp="2wd6tqf8tha2b">
      <div className="container mx-auto px-6 relative z-10 max-w-5xl" data-id="element-668" data-mp="3654cq862h6bl">
        {sectionHeader('Section 16', 'Wedding', 'Planners', 'Immersive storyworlds for the most important celebration of a lifetime.')}

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="border border-[#d4a017]/15 bg-[#0f0f23]/40 p-8 md:p-10 mb-8" data-id="element-669" data-mp="3f47w7d95bnqm">
          <p className="text-[#f5f0e8]/50 font-light leading-relaxed" data-id="element-670" data-mp="1uqh9e9nwa244">
            The immersive technology and storytelling framework behind Life in
            the Sikh Kingdoms can be adapted for luxury weddings. Imagine a
            sangeet where the walls come alive with the couple's story, a
            reception where the room transforms for the grand reveal, or a
            welcome night that transports guests to another world entirely.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-id="element-671" data-mp="usdz07a5ugjc">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="border border-[#d4a017]/10 bg-[#0f0f23]/30 p-6 text-center" data-id="element-672" data-mp="qru294ti1exq">
            <GemIcon className="w-6 h-6 text-[#d4a017]/40 mx-auto mb-3" data-id="element-673" data-mp="31jdcgsh0hghv" />
            <p className="text-[#f5f0e8] text-sm font-medium mb-1" data-id="element-674" data-mp="15gqtd7tu94w4">
              Welcome Night
            </p>
            <p className="text-[#f5f0e8]/30 text-xs font-light" data-id="element-675" data-mp="1ah4ppsm2uqeb">
              Immersive arrival experience
            </p>
          </motion.div>
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.05
        }} className="border border-[#d4a017]/10 bg-[#0f0f23]/30 p-6 text-center" data-id="element-676" data-mp="vl6zeqyu4ga0">
            <GemIcon className="w-6 h-6 text-[#d4a017]/40 mx-auto mb-3" data-id="element-677" data-mp="1jfq8novgo7fm" />
            <p className="text-[#f5f0e8] text-sm font-medium mb-1" data-id="element-678" data-mp="275uaprt03z4i">Sangeet</p>
            <p className="text-[#f5f0e8]/30 text-xs font-light" data-id="element-679" data-mp="2bx2jclmibggm">
              360° story of the couple
            </p>
          </motion.div>
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="border border-[#d4a017]/10 bg-[#0f0f23]/30 p-6 text-center" data-id="element-680" data-mp="h22osynrivz">
            <GemIcon className="w-6 h-6 text-[#d4a017]/40 mx-auto mb-3" data-id="element-681" data-mp="3dqghl0yhvqcr" />
            <p className="text-[#f5f0e8] text-sm font-medium mb-1" data-id="element-682" data-mp="38rddpju93q6d">
              Reception Reveal
            </p>
            <p className="text-[#f5f0e8]/30 text-xs font-light" data-id="element-683" data-mp="3mn7etgos9b8x">
              Grand entrance transformation
            </p>
          </motion.div>
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.15
        }} className="border border-[#d4a017]/10 bg-[#0f0f23]/30 p-6 text-center" data-id="element-684" data-mp="11yb7w7yrykzy">
            <GemIcon className="w-6 h-6 text-[#d4a017]/40 mx-auto mb-3" data-id="element-685" data-mp="wmfci9cpqlio" />
            <p className="text-[#f5f0e8] text-sm font-medium mb-1" data-id="element-686" data-mp="ro44m45zal33">
              Luxury Upsell
            </p>
            <p className="text-[#f5f0e8]/30 text-xs font-light" data-id="element-687" data-mp="15nn7ahg0t480">
              Premium immersive add-on
            </p>
          </motion.div>
        </div>
      </div>
    </section>;
}
/* ─── RESORT SALES ─── */
export function ResortSalesSection() {
  return <section id="resort-sales" className="py-24 md:py-32 bg-[#0a0a0f] relative overflow-hidden" data-id="element-688" data-mp="1ah7zttyuh5zt">
      <div className="absolute inset-0 bg-gradient-to-b from-[#1a1a2e]/8 to-transparent" data-id="element-689" data-mp="1pe8ua58f7f1z"></div>
      <div className="container mx-auto px-6 relative z-10 max-w-5xl" data-id="element-690" data-mp="1kfnqjvtymd8s">
        {sectionHeader('Section 17', 'Resort', 'Sales', 'The Ever After Experience — a premium upsell for resorts and luxury venues.')}

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="border border-[#d4a017]/15 bg-[#0f0f23]/40 p-8 md:p-10 mb-8" data-id="element-691" data-mp="1u28hquxgj201">
          <p className="text-[#f5f0e8]/50 font-light leading-relaxed mb-6" data-id="element-692" data-mp="1bbgyn1no3x8g">
            Resorts and luxury venues can offer the immersive experience as a
            premium upsell — a "Signature Weekend" that transforms a standard
            stay into an unforgettable cultural journey. Centralized creative
            direction with local execution means consistent quality at any
            venue.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-693" data-mp="n8arxadypcoq">
            <div className="border border-[#d4a017]/10 p-4 text-center" data-id="element-694" data-mp="ihc7pzpurug7">
              <p className="text-[#d4a017]/60 text-xs tracking-wider uppercase mb-1" data-id="element-695" data-mp="iezhz0dusfsl">
                Welcome
              </p>
              <p className="text-[#f5f0e8]/50 text-sm font-light" data-id="element-696" data-mp="8j4unryopwjh">
                Immersive arrival
              </p>
            </div>
            <div className="border border-[#d4a017]/10 p-4 text-center" data-id="element-697" data-mp="165bxawbfhet7">
              <p className="text-[#d4a017]/60 text-xs tracking-wider uppercase mb-1" data-id="element-698" data-mp="1170per4p1edm">
                Sangeet
              </p>
              <p className="text-[#f5f0e8]/50 text-sm font-light" data-id="element-699" data-mp="w75v0dlpm6nt">
                Cultural evening
              </p>
            </div>
            <div className="border border-[#d4a017]/10 p-4 text-center" data-id="element-700" data-mp="rbqk9uj00qvm">
              <p className="text-[#d4a017]/60 text-xs tracking-wider uppercase mb-1" data-id="element-701" data-mp="3h09clzyzo3oh">
                Reveal
              </p>
              <p className="text-[#f5f0e8]/50 text-sm font-light" data-id="element-702" data-mp="3c1y1zn0vdp57">
                Grand moment
              </p>
            </div>
            <div className="border border-[#d4a017]/10 p-4 text-center" data-id="element-703" data-mp="1wg49ava1qpjr">
              <p className="text-[#d4a017]/60 text-xs tracking-wider uppercase mb-1" data-id="element-704" data-mp="21fqm53lyq3s1">
                Weekend
              </p>
              <p className="text-[#f5f0e8]/50 text-sm font-light" data-id="element-705" data-mp="266qhw5jcifsy">
                Signature package
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}
/* ─── INVESTORS ─── */
export function InvestorsSection() {
  return <section id="investors" className="py-24 md:py-32 bg-[#0a0a0f] relative overflow-hidden" data-id="element-706" data-mp="2k68a6uvuob72">
      <div className="container mx-auto px-6 relative z-10 max-w-5xl" data-id="element-707" data-mp="2p52rtti2ztpl">
        {sectionHeader('Section 18', 'Investors &', 'Family Offices', 'More than a show — a scalable immersive platform with multiple revenue streams.')}

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="border border-[#d4a017]/15 bg-[#0f0f23]/40 p-8 md:p-10 mb-8" data-id="element-708" data-mp="ut4scj9kqc6w">
          <p className="text-[#f5f0e8]/50 font-light leading-relaxed mb-6" data-id="element-709" data-mp="2q5nr587zpojf">
            Life in the Sikh Kingdoms is not a one-time event — it is a
            category-creating platform. The immersive dining and cultural
            experience format is scalable, licensable, and applicable across
            multiple verticals: tourism, weddings, private events, corporate
            experiences, and cultural licensing.
          </p>
          <p className="text-[#f5f0e8]/50 font-light leading-relaxed" data-id="element-710" data-mp="1it56xein3ykc">
            For investors and family offices, this represents an opportunity to
            participate in the creation of a new category — immersive cultural
            experiences that combine heritage, technology, cuisine, and
            storytelling into a premium, repeatable format with global tour
            potential.
          </p>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-711" data-mp="urs4wwzzlo5z">
          <motion.div initial={{
          opacity: 0,
          y: 15
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="border border-[#d4a017]/10 bg-[#0f0f23]/30 p-5 text-center" data-id="element-712" data-mp="zoijth022dpt">
            <p className="text-[#d4a017] font-serif text-2xl mb-1" data-id="element-713" data-mp="1ie8bvwcfole1">Tourism</p>
            <p className="text-[#f5f0e8]/30 text-xs font-light" data-id="element-714" data-mp="219zhpbsjape">
              Destination experiences
            </p>
          </motion.div>
          <motion.div initial={{
          opacity: 0,
          y: 15
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.05
        }} className="border border-[#d4a017]/10 bg-[#0f0f23]/30 p-5 text-center" data-id="element-715" data-mp="6yjo52rcvic6">
            <p className="text-[#d4a017] font-serif text-2xl mb-1" data-id="element-716" data-mp="1pzkkn07zfpkh">Weddings</p>
            <p className="text-[#f5f0e8]/30 text-xs font-light" data-id="element-717" data-mp="1lxyz6qhdu2yo">
              Luxury immersive events
            </p>
          </motion.div>
          <motion.div initial={{
          opacity: 0,
          y: 15
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="border border-[#d4a017]/10 bg-[#0f0f23]/30 p-5 text-center" data-id="element-718" data-mp="1gzfesuifsz8f">
            <p className="text-[#d4a017] font-serif text-2xl mb-1" data-id="element-719" data-mp="3ivwmwqi1htoe">Private</p>
            <p className="text-[#f5f0e8]/30 text-xs font-light" data-id="element-720" data-mp="3iu3h1df93dhg">
              Corporate & VIP events
            </p>
          </motion.div>
          <motion.div initial={{
          opacity: 0,
          y: 15
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.15
        }} className="border border-[#d4a017]/10 bg-[#0f0f23]/30 p-5 text-center" data-id="element-721" data-mp="3sm94qaygz8qu">
            <p className="text-[#d4a017] font-serif text-2xl mb-1" data-id="element-722" data-mp="39tf12d6ar31x">Licensing</p>
            <p className="text-[#f5f0e8]/30 text-xs font-light" data-id="element-723" data-mp="k5o2dd0cfdwp">
              Global cultural IP
            </p>
          </motion.div>
        </div>
      </div>
    </section>;
}